import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type { AgentManagedExtensionPluginState } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtension, isAgentManagedExtension } from '../../common/utils';

export type AgentManagedExtensionMetaAction =
	| { type: 'OPEN_CONFIG' }
	| { type: 'CLOSE_CONFIG' }
	| { type: 'TOGGLE_CONFIG' }
	| { localId: string; requestId: string; type: 'START_EXECUTION' }
	| { localId: string; requestId: string; type: 'FINISH_EXECUTION' }
	| { aiManagedObject: AiManagedObject; type: 'CACHE_AI_MANAGED_OBJECT' };

export type AgentManagedExtensionMeta =
	| AgentManagedExtensionMetaAction
	| readonly AgentManagedExtensionMetaAction[];

const META_KEY = 'agentManagedExtension';

export const setMeta = (tr: Transaction, meta: AgentManagedExtensionMeta): Transaction =>
	tr.setMeta(META_KEY, meta);

export const getMeta = (
	tr: Transaction | ReadonlyTransaction,
): AgentManagedExtensionMeta | undefined =>
	tr.getMeta(META_KEY) as AgentManagedExtensionMeta | undefined;

export const initialState = (): AgentManagedExtensionPluginState => ({
	aiManagedObjectsById: {},
	executingLocalIds: {},
	isConfigOpen: false,
});

const collectLocalIds = (doc: EditorState['doc']): Set<string> => {
	const found = new Set<string>();
	doc.descendants((node) => {
		if (!isAgentManagedExtension(node)) {
			return true;
		}
		const localId = node.attrs.localId as string | undefined;
		if (localId) {
			found.add(localId);
		}
		return false;
	});
	return found;
};

const findNewLocalIds = (oldState: EditorState, newState: EditorState): string[] => {
	const oldLocalIds = collectLocalIds(oldState.doc);
	const newLocalIds = collectLocalIds(newState.doc);
	return [...newLocalIds].filter((localId) => !oldLocalIds.has(localId));
};

const isLocalUserInsertTransaction = (tr: Transaction | ReadonlyTransaction): boolean =>
	tr.docChanged &&
	!tr.getMeta('isRemote') &&
	!tr.getMeta('replaceDocument') &&
	// Editor transactions do not carry a single "local user insert" bit. For
	// auto-open we treat history-tracked document changes as user-authored and
	// exclude remote/document-replacement transactions above.
	tr.getMeta('addToHistory') !== false;

const getSelectedAgentManagedLocalId = (state: EditorState): string | undefined =>
	findAgentManagedExtension(state)?.node.attrs.localId as string | undefined;

const cacheAiManagedObject = (
	prev: AgentManagedExtensionPluginState,
	aiManagedObject: AiManagedObject,
): AgentManagedExtensionPluginState => ({
	...prev,
	aiManagedObjectsById: {
		...prev.aiManagedObjectsById,
		[aiManagedObject.id]: aiManagedObject,
	},
});

const closeConfig = (
	prev: AgentManagedExtensionPluginState,
): AgentManagedExtensionPluginState => ({
	...prev,
	configLocalId: undefined,
	isConfigOpen: false,
	pendingAutoOpenLocalId: undefined,
});

const startExecution = (
	prev: AgentManagedExtensionPluginState,
	localId: string,
	requestId: string,
): AgentManagedExtensionPluginState => ({
	...prev,
	executingLocalIds: {
		...prev.executingLocalIds,
		[localId]: requestId,
	},
});

const finishExecution = (
	prev: AgentManagedExtensionPluginState,
	localId: string,
	requestId: string,
): AgentManagedExtensionPluginState => {
	if (prev.executingLocalIds[localId] !== requestId) {
		return prev;
	}

	const { [localId]: _removed, ...executingLocalIds } = prev.executingLocalIds;
	return {
		...prev,
		executingLocalIds,
	};
};

const applyMetaAction = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMetaAction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	switch (meta.type) {
		case 'CACHE_AI_MANAGED_OBJECT':
			return cacheAiManagedObject(prev, meta.aiManagedObject);
		case 'START_EXECUTION':
			return startExecution(prev, meta.localId, meta.requestId);
		case 'FINISH_EXECUTION':
			return finishExecution(prev, meta.localId, meta.requestId);
		case 'OPEN_CONFIG': {
			const configLocalId = getSelectedAgentManagedLocalId(newState);
			if (!configLocalId) {
				return { ...prev, configLocalId: undefined, isConfigOpen: false };
			}
			return {
				...prev,
				configLocalId,
				isConfigOpen: true,
				pendingAutoOpenLocalId: undefined,
			};
		}
		case 'CLOSE_CONFIG':
			return closeConfig(prev);
		case 'TOGGLE_CONFIG': {
			if (prev.isConfigOpen) {
				return closeConfig(prev);
			}
			const configLocalId = getSelectedAgentManagedLocalId(newState);
			if (!configLocalId) {
				return { ...prev, configLocalId: undefined, isConfigOpen: false };
			}
			return {
				...prev,
				configLocalId,
				isConfigOpen: true,
				pendingAutoOpenLocalId: undefined,
			};
		}
		default:
			return prev;
	}
};

/**
 * Pure reducer for AgentManagedExtensionMeta actions.
 */
export const applyMeta = (
	prev: AgentManagedExtensionPluginState,
	meta: AgentManagedExtensionMeta | undefined,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!meta) {
		return prev;
	}
	const actions = Array.isArray(meta) ? meta : [meta];
	return actions.reduce(
		(nextState, action) => applyMetaAction(nextState, action, newState),
		prev,
	);
};

/**
 * Marks a locally inserted agent-managed block for config auto-open.
 * Existing content loaded through document replacement/collab transactions
 * should never auto-open config.
 */
export const applyAutoOpenOnInsert = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	oldState: EditorState,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!isLocalUserInsertTransaction(tr)) {
		return prev;
	}

	const newLocalIds = findNewLocalIds(oldState, newState);
	if (newLocalIds.length !== 1) {
		return prev;
	}

	return {
		...prev,
		pendingAutoOpenLocalId: newLocalIds[0],
	};
};

/**
 * Returns `{ isConfigOpen: false }` when the popup is open but the
 * selection has moved away from the configured block. Otherwise returns
 * `prev` unchanged.
 */
export const applyAutoCloseOnSelectionChange = (
	prev: AgentManagedExtensionPluginState,
	tr: Transaction | ReadonlyTransaction,
	newState: EditorState,
): AgentManagedExtensionPluginState => {
	if (!prev.isConfigOpen || !tr.selectionSet) {
		return prev;
	}
	const selectedLocalId = getSelectedAgentManagedLocalId(newState);
	if (selectedLocalId && selectedLocalId === prev.configLocalId) {
		return prev;
	}
	return {
		...prev,
		configLocalId: undefined,
		isConfigOpen: false,
		pendingAutoOpenLocalId: undefined,
	};
};
